import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CREATE_GROUP_TEMPLATE } from '../../../api/mutations/groupTemplates';
import { QUOTATION_QUERY } from '../../../api/queries/quotations/quotation';
import { useAppDispatch, useAppSelector } from '../../../helpers/reduxHooks';
import { classNames } from '../../../helpers/utils';
import LoadingIndicator from '../../../layout/LoadingIndicator';
import { setSuccessAlert } from '../../../redux/alertSlice';
import { setIsLoadingUpdate } from '../../../redux/quotationSlice';

interface Props {  
  handleClose: () => void,
}

export default function CreateGTConfirmationModal(props: Props) {
  const { handleClose } = props;
  const dispatch = useAppDispatch();
  const { quotationId } = useParams();
  const { t } = useTranslation();
  const itemIdToCreateGTFrom = useAppSelector(state => state.quotation.itemIdToCreateGTFrom);

  const [
    createGroupTemplate,
    {
      data: mutationData,
      loading: mutationLoading,
    },
  ] = useMutation(CREATE_GROUP_TEMPLATE, {
    refetchQueries: [{
      query: QUOTATION_QUERY,
      variables: {
        quotation: quotationId as string,
      },
    }],
  });

  const handleStoreGroupAsTemplate = () => {
    createGroupTemplate(
      {
        variables: {
          item: itemIdToCreateGTFrom,
        },
        onError: () => {
          dispatch(setIsLoadingUpdate(false));
        },
      },
    );
  };

  useEffect(() => {
    if (mutationData && mutationData.createGroupTemplate && mutationData.createGroupTemplate.response) {
      dispatch(setSuccessAlert(
        ['Group Template was successfully created.'],
      ));
      handleClose();
    }
  }, [mutationData, dispatch, handleClose]);

  return (
    <div className='flex flex-col justify-between items-center w-full rounded drop-shadow-xl bg-white' onClick={e => e.stopPropagation()}>
      <div className='pl-8 bg-cblue-100 w-full h-14 border-b-2 border-cblue-500 font-bold flex items-center text-cblue-500'>{t('Create Group Template')}</div>
      <div className='w-full pl-8 my-8'>{t('This group (or any of nested groups) has percentage type of manual price. This price is going to be saved as Absolute price.')}</div>
      
      <div className='flex justify-end gap-8 pr-8 pb-8 w-full'>
        <button
          onClick={() => !mutationLoading && handleClose()}
          type="button"
          className={classNames('w-1/5 flex justify-center items-center px-4 py-2 border border-cblue-500 rounded text-sm font-medium uppercase text-cblue-500', 
            mutationLoading ? 'cursor-wait bg-cgray-300 text-white border-none' : 'hover:bg-cblue-200 focus:bg-cblue-300 active:bg-cblue-300',
          )}
        >
          {t('Cancel')}
        </button>
        <button
          disabled={mutationLoading}
          onClick={handleStoreGroupAsTemplate}
          type="submit"
          className={classNames('w-1/5 relative flex justify-center items-center px-4 py-2 rounded text-sm font-medium uppercase text-white bg-cblue-500 transition duration-150', 
            mutationLoading ? 'cursor-wait bg-cgray-300 text-white' : 'hover:bg-cblue-700 hover:opacity-90 focus:bg-cblue-700 focus:opacity-100 active:bg-cblue-700 active:opacity-100')}
        >
          {mutationLoading ? <LoadingIndicator color="white" className="flex justify-center items-center absolute w-full h-full top-0 right-0" /> : t('Confirm')}
        </button>
      </div>
    </div>
  );
}
